/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  Grid,
  Box,
  Container,
  useTheme,
  useMediaQuery,
  Button,
  Typography,
  Modal,
  FormHelperText,
  FormControlLabel,
} from "@mui/material";
import { RootState, useAppDispatch } from "@store/store";
import { useValidatePhoneCodeMutation, useRequestPhoneCodeMutation } from "@api/onboarding";
import { formatTime, formatPhoneNumber } from "@helpers/funciones";
import "@styles/stylesRegistro.css";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { InactivityHandler } from "@components/InactivityHandler";
import { AddressData, ApiError, PersonaStructure } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";
import ModalBuroNipAvalAdicional from "@components/ModalBuroNipAvalAdicional";
import { CustomSwitch } from "@components/CustomSwitch";
import { CustomTextCiecBuro } from "@components/CustomTextCiecBuro";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useLazyGetPersonByIdQuery } from "@api/personasApi";
import { Address } from "@interfaces/catalogs";
import { useLazyGetAddressQuery } from "@api/address";
import { useLazyGetAddressTypeQuery } from "@api/catalogs";
import { ADDRESS_CODES } from "@helpers/constantes";
import { PhoneCodeInput } from "@components/PhoneCodeInput";
import { setAvalAdicionalBuroInfo } from "@store/slices/bofuSlice";

const TIME_LIMIT = 120;

const ConsultaBuroNip = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const buroNip = useSelector((state: RootState) => state.register.buroNip);
  const avalAdicionalId = useSelector((state: RootState) => state.bofu.aval_adicional_id_information);
  const avalAdicionalBuroInfo = useSelector((state: RootState) => state.bofu.aval_adicional_buro_info);

  const [validatePhoneCode] = useValidatePhoneCodeMutation();
  const [requestPhoneCode, { isLoading: isRequestingCode }] = useRequestPhoneCodeMutation();
  const [getPersonById] = useLazyGetPersonByIdQuery();
  const [getAddress] = useLazyGetAddressQuery();
  const [getAddresType] = useLazyGetAddressTypeQuery();

  const [secondsRemaining, setSecondsRemaining] = useState(TIME_LIMIT);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const [avalSelected, setAvalSelected] = useState<PersonaStructure | null>(null);
  const [addressSelected, setAddressSelected] = useState<AddressData | null>(null);

  const formik = useFormik({
    initialValues: {
      code: "",
      switch: buroNip?.termsAccepted || false,
    },
    validationSchema: yup.object().shape({
      code: yup.string().length(4, "El código debe tener 4 dígitos"),
      switch: yup.boolean().oneOf([true], "Debes aceptar los términos y condiciones"),
    }),
    onSubmit: async () => {
      setOpenModalConfirm(true);
    },
  });

  const handleResendCode = async () => {
    try {
      if (avalAdicionalBuroInfo?.is_valid) {
        return;
      }

      if (!avalSelected) {
        return;
      }

      if (isRequestingCode) {
        return;
      }

      setIsLoading(true);

      await requestPhoneCode({
        country_code: avalSelected?.code_phone_number || "",
        phone_number: avalSelected?.phone_number || "",
      }).unwrap();

      formik.setFieldValue("code", "");

      // save last request time in redux
      dispatch(setAvalAdicionalBuroInfo({ last_request: dayjs().toISOString(), is_valid: false, code: "" }));
      setSecondsRemaining(TIME_LIMIT);
    } catch (error) {
      const api_error = error as ApiError;
      if (api_error?.status === 429) {
        handleOpen();
        return;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const validateCode = async (fullCode: string) => {
    try {
      if (fullCode.length < 4 || !avalAdicionalId) {
        return;
      }

      if (avalAdicionalBuroInfo?.is_valid) {
        return;
      }

      setIsLoading(true);
      setErrorMessage(null);

      formik.setFieldValue("code", fullCode);

      await validatePhoneCode({
        country_code: avalSelected?.code_phone_number || "",
        phone_number: avalSelected?.phone_number || "",
        code: fullCode,
      }).unwrap();

      dispatch(setAvalAdicionalBuroInfo({ is_valid: true, code: fullCode }));
    } catch (error) {
      const api_error = error as ApiError;
      if (api_error?.status === 404 || api_error?.status === 400) {
        setErrorMessage("Código incorrecto, intenta de nuevo");
        formik.setFieldValue("code", "");
        return;
      }

      dispatch(setModalErrorVisible({ open: true, error: api_error }));
      Sentry.captureException(api_error, {
        extra: {
          component: "ValidacionTelefono",
          values: formik.values,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  useEffect(() => {
    (async () => {
      try {
        if (!avalAdicionalId) {
          navigate("/producto/creditosimple/docs_aval");
          return;
        }

        setIsLoading(true);

        const [aval_selected, res_address_type, res_address] = await Promise.all([
          getPersonById(avalAdicionalId).unwrap(),
          getAddresType().unwrap(),
          getAddress({
            personId: avalAdicionalId,
          }).unwrap(),
        ]);

        if (!aval_selected) {
          navigate("/producto/creditosimple/docs_aval");
          return;
        }

        const address_type = res_address_type.find((item: Address) => item.code === ADDRESS_CODES.particular.code);
        const address = res_address.find((item: AddressData) => item.address_type_id === address_type?.id);

        if (!address) {
          navigate("/producto/creditosimple/docs_aval");
          return;
        }

        setAvalSelected(aval_selected);
        setAddressSelected(address);
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "AvalAdicionalBuroP4-initial_fetch",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!avalSelected || !addressSelected) {
        return;
      }

      if (avalAdicionalBuroInfo?.is_valid) {
        formik.setFieldValue("code", avalAdicionalBuroInfo?.code || "");
        formik.setFieldTouched("code", true);
        formik.validateForm();
        return;
      }

      // get last request time from redux
      if (avalAdicionalBuroInfo?.last_request) {
        // calculate seconds from last request and seconds to wait to send code again
        const lastRequestTimeDate = dayjs(avalAdicionalBuroInfo?.last_request);
        const currentTime = dayjs();
        const secondsFromLastRequest = currentTime.diff(lastRequestTimeDate, "seconds");
        const secondsToWait = TIME_LIMIT - secondsFromLastRequest;

        // if seconds from last request are greater than TIME_LIMIT, send code again
        if (secondsFromLastRequest > TIME_LIMIT) {
          handleResendCode();
        } else {
          setSecondsRemaining(secondsToWait);
        }
      } else {
        // code has not been requested yet
        handleResendCode();
      }
    })();
  }, [avalSelected, addressSelected]);

  // timer
  useEffect(() => {
    if (isLoading) {
      return;
    }

    const intervalId = setInterval(() => {
      if (secondsRemaining > 0) {
        setSecondsRemaining(secondsRemaining - 1);
      } else {
        clearInterval(intervalId);
      }
    }, 1000);
    return () => clearInterval(intervalId);
  }, [secondsRemaining, isLoading]);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />
        <Box
          sx={{
            mt: 2,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            textAlign="center"
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={700}
            color={theme?.palette?.primary?.dark}
          >
            Información del aval
          </Typography>

          <Typography
            textAlign={"center"}
            color={theme?.palette?.primary?.main}
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={600}
            mt={2}
          >
            Te solicitamos ingresar un segundo aval a tu solicitud.
          </Typography>

          <Typography
            mt={2}
            textAlign="center"
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.main}
            fontWeight={600}
          >
            {avalSelected?.name} {avalSelected?.last_name} {avalSelected?.last_name_2}
          </Typography>

          <Box maxWidth={"sm"} mt={isMobileOrTablet ? 2 : 3} textAlign={"center"}>
            <Typography variant={isMobileOrTablet ? "body2" : "body1"} color={theme?.palette?.primary?.dark}>
              Ingresa el código que enviamos vía WhatsApp al número
            </Typography>
          </Box>

          <Typography
            variant={isMobileOrTablet ? "h5" : "h4"}
            mt={isMobileOrTablet ? 2 : 3}
            fontWeight={600}
            color={theme?.palette?.primary?.main}
          >
            ({avalSelected?.code_phone_number}) {formatPhoneNumber(avalSelected?.phone_number || "")}
          </Typography>

          <Typography
            mt={2}
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={300}
            color={theme?.palette?.primary?.dark}
          >
            Código {buroNip?.isValid ? "verificado" : "no verificado"}
          </Typography>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 1 }}>
            <form onSubmit={formik.handleSubmit} noValidate>
              <PhoneCodeInput
                name="code"
                value={formik.values.code}
                onBlur={formik.handleBlur}
                disabled={isLoading || formik.isSubmitting || avalAdicionalBuroInfo?.is_valid}
                onComplete={validateCode}
              />

              {errorMessage && <FormHelperText sx={{ mt: isMobileOrTablet ? 2 : 1 }}>{errorMessage}</FormHelperText>}

              {!avalAdicionalBuroInfo?.is_valid && (
                <>
                  <Typography
                    variant={isMobileOrTablet ? "body2" : "body1"}
                    mt={isMobileOrTablet ? 2 : 3}
                    textAlign="center"
                    fontWeight={600}
                    color={theme?.palette?.primary?.main}
                  >
                    {formatTime(secondsRemaining)} min
                  </Typography>

                  {secondsRemaining === 0 && (
                    <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3}>
                      <Typography
                        variant={isMobileOrTablet ? "body2" : "body1"}
                        component={"a"}
                        onClick={handleResendCode}
                        sx={{ cursor: "pointer", textDecoration: "underline" }}
                        color={theme?.palette?.primary?.dark}
                      >
                        Reenviar código
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              <Box display={"flex"} justifyContent={"center"} mt={2} textAlign="center">
                <FormControlLabel
                  control={<CustomSwitch />}
                  label={
                    <Box sx={{ paddingTop: "10px" }}>
                      <Typography variant="body2" color={theme?.palette?.primary?.dark}>
                        <CustomTextCiecBuro type="nipBuro" />
                      </Typography>
                    </Box>
                  }
                  name="switch"
                  checked={formik.values.switch}
                  onChange={formik.handleChange}
                  disabled={isLoading || formik.isSubmitting}
                />
              </Box>

              <Grid container spacing={1} my={1}>
                <Grid item xs={6} sm={6} md={6}>
                  <Button
                    variant="blue_outlined"
                    type="button"
                    fullWidth
                    onClick={() => {
                      navigate("/producto/creditosimple/docs_aval");
                    }}
                    disabled={isLoading || formik.isSubmitting}
                  >
                    Regresar al inicio
                  </Button>
                </Grid>

                <Grid item xs={6} sm={6} md={6}>
                  <Button
                    variant="default"
                    type="submit"
                    fullWidth
                    disabled={isLoading || !formik.isValid || formik.isSubmitting}
                  >
                    Continuar
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}

      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Typography fontWeight={"600"} gutterBottom color={theme?.palette?.primary?.dark} fontSize={"20px"}>
            Excedió el número de intentos permitidos para ese número
          </Typography>
          <Typography fontSize={"16px"} sx={{ mt: 2, mb: 2 }} color={theme?.palette?.primary?.dark}>
            intenta más tarde o contacta a un asesor.
          </Typography>
          <Button variant="default" color="primary" onClick={handleClose}>
            Cerrar
          </Button>
        </Box>
      </Modal>

      {avalSelected && addressSelected && (
        <ModalBuroNipAvalAdicional
          expectedCode={formik.values.code}
          avalAdicional={avalSelected}
          address={addressSelected}
          open={openModalConfirm}
          onClose={() => setOpenModalConfirm(false)}
        />
      )}
    </Grid>
  );
};

export default ConsultaBuroNip;
